import z from 'zod';
/**
 * A project's engine thrust-conversion history (DR-3127) — every "rating changed from X to Y"
 * the project's documents assert about its working engine.
 *
 * Emitted as ONE webhook per project carrying a flat array, mirroring the engine-events family:
 * an engine can be converted more than once, and each conversion is normally evidenced by its
 * own bulletin in its own document, so there is no single winning document to pick. Each row
 * keeps the `pageId` it was extracted from.
 *
 * `esn` is the project's working engine, the same serial the rows were scoped to — nullable
 * for the same reason the engine family's is.
 */
export const thrustConversionEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('thrust-conversion-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // `.optional()` is a ROLLOUT concession, not a statement that the stamp is dispensable —
    // Brain always emits it (see the family's builder, pinned by its test). This schema is a
    // member of `webhookPayloadSchema`, so a REQUIRED field would make a consumer that
    // upgrades `@avisoma/shared` reject every older, unstamped envelope still in flight — and reject
    // ALL of them for as long as the consumer runs ahead of Brain's own deploy. Tighten to
    // required in a later release, once no unstamped envelope can still be in flight (DR-3278).
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    esn: z.string().nullable(),
    thrustConversionEvents: z.array(z.looseObject({
        // BOTH ratings stay required on every row (DR-4444): the page layer admits a row that prints
        // only the target rating, but the project-level generator fills its `from` from the engine's
        // previous conversion (or, for the first conversion, the engine history) or drops it before
        // the webhook is built.
        fromThrustRating: z.string(),
        toThrustRating: z.string(),
        // 'previous-conversion' when `fromThrustRating` was filled from the engine's previous
        // conversion rather than printed on the row's page (DR-4444); 'engine-history' when the
        // engine's FIRST conversion took it from the rating in effect on the nearest earlier engine
        // install/removal history row (DR-4692). Absent when printed.
        fromThrustRatingSource: z.enum(['previous-conversion', 'engine-history']).optional(),
        conversionDate: z.string().optional(),
        // The verbatim caption / sign-off text `conversionDate` was grounded on (DR-4452).
        // Present only alongside `conversionDate`; reviewer evidence, never an identity field.
        conversionDateLabel: z.string().optional(),
        // 'engine-history' when `conversionDate` was taken from the project's engine install/removal
        // history by work-order match rather than printed on the row's page (DR-4608). Absent when
        // printed. Per field: a row can carry a printed `from` and a history date.
        conversionDateSource: z.literal('engine-history').optional(),
        authorizingDoc: z.string().optional(),
        // The work order the row's page belongs to, verbatim (DR-4608). Evidence for the
        // engine-history date join, never an identity field.
        workOrder: z.string().optional(),
        // The row's OWN serial as printed on its source page, which can spell the working
        // engine differently than the project's voted `esn` above.
        esn: z.string().optional(),
        pageId: z.string(),
    })),
});
//# sourceMappingURL=thrust-conversion-events-ready.webhook.js.map